import {
	Bar,
	BarChart,
	LabelList,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	type LabelProps,
} from 'recharts';
import { ChartCard } from '@practium/features/dashboard';
import type { BillingCashFlowResponse } from '@practium/features/api';
import { buildCashFlowSummary, formatBRL, formatCompactValue } from '../utils/cashFlow.utils';
import { BillingTrend } from './BillingTrend';
import { BillingCashFlowChartSkeleton } from './BillingCashFlowChartSkeleton';
import styles from './BillingCashFlowChart.module.css';

interface BillingCashFlowChartProps {
	title: string;
	subtitle: string;
	trendLabel: string;
	buttonText: string;
	onButtonClick: () => void;
	data: BillingCashFlowResponse | undefined;
	emptyMessage?: string;
	isLoading?: boolean;
}

interface CashFlowPoint {
	day: number;
	inflow: number;
	outflow: number;
}

interface CashFlowTooltipProps {
	active?: boolean;
	payload?: Array<{ payload: CashFlowPoint }>;
}

type FlowKind = 'inflow' | 'outflow';

const FLOW_LABELS: Record<FlowKind, string> = {
	inflow: 'Entradas',
	outflow: 'Saídas',
};

const LegendItem = ({ kind }: { kind: FlowKind }) => (
	<div className={styles.legendItem}>
		<span className={`${styles.legendSquare} ${styles[kind]}`} />
		<span className={styles.legendLabel}>{FLOW_LABELS[kind]}</span>
	</div>
);

const CashFlowTooltip = ({ active, payload }: CashFlowTooltipProps) => {
	if (!active || !payload || payload.length === 0) return null;

	const point = payload[0].payload;

	return (
		<div className={styles.tooltip}>
			<span className={styles.tooltipTitle}>Dia {point.day}</span>
			{(['inflow', 'outflow'] as FlowKind[]).map((kind) => (
				<div key={kind} className={styles.tooltipRow}>
					<LegendItem kind={kind} />
					<span className={styles.tooltipValue}>{formatBRL(Math.abs(point[kind]))}</span>
				</div>
			))}
		</div>
	);
};

const renderPeakLabel =
	(peakIndex: number) =>
	({ x, y, width, index, value }: LabelProps & { index?: number }) => {
		if (index !== peakIndex) return null;

		return (
			<text
				x={Number(x) + Number(width) / 2}
				y={Number(y) - 8}
				textAnchor="middle"
				className={styles.peakLabel}
			>
				{formatCompactValue(Number(value))}
			</text>
		);
	};

export const BillingCashFlowChart = ({
	title,
	subtitle,
	trendLabel,
	buttonText,
	onButtonClick,
	data,
	emptyMessage,
	isLoading,
}: BillingCashFlowChartProps) => {
	const days = data?.days ?? [];
	const isEmpty = !data || (data.totalInflow === 0 && data.totalOutflow === 0);
	const chartData = days.map((day) => ({ ...day, outflow: -day.outflow }));
	const peakIndex = data?.peakInflow ? days.findIndex((day) => day.day === data.peakInflow?.day) : -1;

	return (
		<ChartCard
			title={title}
			subtitle={subtitle}
			buttonText={buttonText}
			onButtonClick={onButtonClick}
			isEmpty={isEmpty}
			emptyMessage={emptyMessage}
			isLoading={isLoading}
			skeleton={<BillingCashFlowChartSkeleton />}
		>
			{data && (
				<div className={styles.layout}>
					<div className={styles.body}>
						<div className={styles.summary}>
							<div className={styles.balance}>
								<span className={styles.balanceLabel}>Saldo do mês</span>
								<span className={styles.balanceValue}>{formatBRL(data.balance, false)}</span>
							</div>

							<div className={styles.totals}>
								<BillingTrend
									value={`${data.trendPercent.toFixed(1)}%`}
									label={trendLabel}
									direction={data.trendDirection}
								/>
								<div className={styles.totalRow}>
									<LegendItem kind="inflow" />
									<span className={styles.totalValue}>{formatBRL(data.totalInflow)}</span>
								</div>
								<div className={styles.totalRow}>
									<LegendItem kind="outflow" />
									<span className={styles.totalValue}>{formatBRL(data.totalOutflow)}</span>
								</div>
							</div>

							<p className={styles.insight}>
								{buildCashFlowSummary(data.peakInflow, data.peakOutflow)}
							</p>
						</div>

						<div className={styles.chartArea}>
							<div className={styles.chartContainer}>
								<ResponsiveContainer width="100%" height="100%">
									<BarChart
										data={chartData}
										stackOffset="sign"
										barCategoryGap="35%"
										margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
									>
										<XAxis
											dataKey="day"
											axisLine={false}
											tickLine={false}
											tickMargin={12}
											interval="preserveStartEnd"
											minTickGap={4}
											tick={{ fill: 'var(--text-tertiary)', fontSize: '0.6875rem' }}
										/>
										<ReferenceLine y={0} stroke="var(--billing-cashflow-divider)" />
										<Tooltip content={<CashFlowTooltip />} cursor={false} />
										<Bar
											dataKey="inflow"
											stackId="flow"
											fill="var(--billing-cashflow-inflow)"
											radius={4}
											maxBarSize={16}
										>
											<LabelList dataKey="inflow" content={renderPeakLabel(peakIndex)} />
										</Bar>
										<Bar
											dataKey="outflow"
											stackId="flow"
											fill="var(--billing-cashflow-outflow-bar)"
											radius={4}
											maxBarSize={16}
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>

							<div className={styles.legend}>
								<LegendItem kind="inflow" />
								<LegendItem kind="outflow" />
							</div>
						</div>
					</div>
				</div>
			)}
		</ChartCard>
	);
};
